import React, { useState } from "react"
import styled from "styled-components"

import { breakpoints } from "../parameters/theme"

const Container = styled.div`
  background: #f6f6f6;
  margin-bottom: 2em;
  margin-top: 2em !important;
  padding: 1em;

  .input {
    display: grid;
    grid-auto-rows: 1fr;
    grid-gap: 1em 1em;
    grid-template-columns: 1fr 1fr;

    .control {
      position: relative;
    }

    .control input,
    .control select {
      font-size: 0.8em;
      height: 3em;
      padding: 0 1em;
      width: 100%;
    }

    .control input {
      -moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    .control button,
    .control span {
      color: #999999;
      position: absolute;
      right: 0.5em;
      top: 0.8em;
    }
    .control button {
      background: none;
      border: none;
      color: #337ab7;
      cursor: pointer;
      font: inherit;
      margin: 0;
      padding: 0;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .result {
    margin-top: 1em;
    text-align: center;
    font-size: 2em;

    .empty {
      font-style: italic;
    }
  }

  .extra-result {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    margin-top: 2em;

    .explanation {
      color: #999999;
      flex-basis: 100%;
      font-size: 0.8em;
      font-style: italic;
      margin-top: 1.25em;
      text-align: center;
    }
  }

  @media (max-width: ${breakpoints.tablet}px) {
    .extra-result {
      .light,
      .moderate,
      .heavy,
      .vigorous {
        flex-basis: 50%;
        text-align: center;
      }
    }
  }
`

const cmToIn = 0.393701,
  kgToLb = 2.20462

const TDEECalculator = props => {
  const [data, setData] = useState({
    height: "",
    heightMetric: true,
    weight: "",
    weightMetric: true,
    age: "",
    male: 0,
    tdee: 0,
  })

  const updateTdee = options => {
    options = options ? options : {}
    let tdee

    setData(currentData => {
      const newData = { ...currentData, ...options }

      if (
        !newData.height ||
        !newData.weight ||
        !newData.age ||
        isNaN(newData.height) ||
        isNaN(newData.weight) ||
        isNaN(newData.age)
      ) {
        tdee = 0
      } else {
        const weightKg = newData.weight / (newData.weightMetric ? 1 : kgToLb)
        const heightCm = newData.height / (newData.heightMetric ? 1 : cmToIn)

        tdee =
          9.99 * weightKg +
          6.25 * heightCm +
          4.92 * newData.age +
          166 * newData.male -
          161
      }

      return { ...newData, tdee }
    })
  }

  const useInches = () => {
    const options = {
      heightMetric: false,
    }

    if (data.height && !isNaN(data.height)) {
      options.height = Math.round(data.height * cmToIn)
    }

    updateTdee(options)
  }

  const useCentimeters = () => {
    const options = {
      heightMetric: true,
    }

    if (data.height && !isNaN(data.height)) {
      options.height = Math.round(data.height / cmToIn)
    }

    updateTdee(options)
  }

  const usePounds = () => {
    const options = {
      weightMetric: false,
    }

    if (data.weight && !isNaN(data.weight)) {
      options.weight = Math.round(data.weight * kgToLb)
    }

    updateTdee(options)
  }

  const useKilos = () => {
    const options = {
      weightMetric: true,
    }

    if (data.weight && !isNaN(data.weight)) {
      options.weight = Math.round(data.weight / kgToLb)
    }

    updateTdee(options)
  }

  const round = value => {
    return Math.round(value)
  }

  const handleInputChange = e => {
    const prop = e.currentTarget.name

    updateTdee({
      [prop]: e.currentTarget.value,
    })
  }

  return (
    <Container id="tdee-calculator">
      <div className="input">
        <div className="control">
          <input
            type="number"
            onChange={handleInputChange}
            value={data.height}
            name="height"
            placeholder="height"
            tabIndex={0}
          />
          {data.heightMetric ? (
            <button onClick={useInches} tabIndex={-1}>
              cm
            </button>
          ) : (
            <button onClick={useCentimeters} tabIndex={-1}>
              in
            </button>
          )}
        </div>
        <div className="control">
          <input
            type="number"
            onChange={handleInputChange}
            value={data.weight}
            name="weight"
            placeholder="weight"
            tabIndex={0}
          />
          {data.weightMetric ? (
            <button onClick={usePounds} tabIndex={-1}>
              kg
            </button>
          ) : (
            <button onClick={useKilos} tabIndex={-1}>
              lbs
            </button>
          )}
        </div>
        <div className="control">
          <input
            type="number"
            onChange={handleInputChange}
            value={data.age}
            name="age"
            placeholder="age"
            tabIndex={0}
          />
          <span>years</span>
        </div>
        <div className="control">
          <select
            onChange={handleInputChange}
            onBlur={handleInputChange}
            value={data.male}
            name="male"
            tabIndex={0}
          >
            <option value="0">Female</option>
            <option value="1">Male</option>
          </select>
        </div>
      </div>
      <div className="result">
        {data.tdee ? (
          <p className="sedentary">{round(data.tdee * 1.2)} kcal per day</p>
        ) : (
          <p className="empty">Enter your numbers above</p>
        )}
      </div>
      {data.tdee ? (
        <div className="extra-result">
          <p className="light">{round(data.tdee * 1.375)} light</p>
          <p className="moderate">{round(data.tdee * 1.55)} moderate</p>
          <p className="heavy">{round(data.tdee * 1.725)} heavy</p>
          <p className="vigorous">{round(data.tdee * 1.9)} vigorous</p>
          <p className="explanation">
            The {round(data.tdee * 1.2)} kcal above is based on a sedentary
            lifestyle. TDEE increases with your activity level, however, so
            select a higher estimate if you lead a more active life – light,
            moderate, etc.
          </p>
        </div>
      ) : (
        ""
      )}
    </Container>
  )
}

export default TDEECalculator
